export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export enum LogCategory {
  USER_INTERACTION = 'user_interaction',
  API_CALL = 'api_call',
  NAVIGATION = 'navigation',
  PERFORMANCE = 'performance',
  ERROR = 'error',
  SEARCH = 'search',
  UI_EVENT = 'ui_event',
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: any;
  sessionId: string;
  userId?: string;
  userAgent: string;
  url: string;
  referrer: string;
}

class Logger {
  private sessionId: string;
  private userId?: string;
  private logLevel: LogLevel;
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.logLevel = process.env.NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO;
    this.initializeSession();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private initializeSession(): void {
    this.info(LogCategory.USER_INTERACTION, 'Session started', {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
      },
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
    });
  }

  private createLogEntry(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: any
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      sessionId: this.sessionId,
      userId: this.userId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private addLog(entry: LogEntry): void {
    this.logs.push(entry);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console output for development
    if (process.env.NODE_ENV === 'development') {
      const logMethod = this.getConsoleMethod(entry.level);
      logMethod(
        `[${entry.category}] ${entry.message}`,
        entry.data ? entry.data : ''
      );
    }

    // Send to analytics service in production (if configured)
    if (process.env.NODE_ENV === 'production') {
      this.sendToAnalytics(entry);
    }
  }

  private getConsoleMethod(level: LogLevel): (...args: any[]) => void {
    switch (level) {
      case LogLevel.DEBUG:
        return console.debug;
      case LogLevel.INFO:
        return console.info;
      case LogLevel.WARN:
        return console.warn;
      case LogLevel.ERROR:
        return console.error;
      default:
        return console.log;
    }
  }

  private sendToAnalytics(entry: LogEntry): void {
    // This could be extended to send to services like Google Analytics, Mixpanel, etc.
    // For now, we'll just store it locally or send to a custom endpoint
    try {
      // Example: Send to custom analytics endpoint
      // fetch('/api/analytics', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(entry),
      // });
    } catch (error) {
      console.error('Failed to send analytics:', error);
    }
  }

  // Public logging methods
  debug(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      this.addLog(this.createLogEntry(LogLevel.DEBUG, category, message, data));
    }
  }

  info(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      this.addLog(this.createLogEntry(LogLevel.INFO, category, message, data));
    }
  }

  warn(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      this.addLog(this.createLogEntry(LogLevel.WARN, category, message, data));
    }
  }

  error(category: LogCategory, message: string, data?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      this.addLog(this.createLogEntry(LogLevel.ERROR, category, message, data));
    }
  }

  // Specialized logging methods for common use cases
  logUserInteraction(action: string, element?: string, data?: any): void {
    this.info(LogCategory.USER_INTERACTION, `User ${action}`, {
      action,
      element,
      ...data,
    });
  }

  logNavigation(from: string, to: string, method: 'click' | 'browser' | 'programmatic' = 'click'): void {
    this.info(LogCategory.NAVIGATION, `Navigation: ${from} -> ${to}`, {
      from,
      to,
      method,
    });
  }

  logApiCall(endpoint: string, method: string, duration?: number, status?: number, data?: any): void {
    this.info(LogCategory.API_CALL, `API ${method} ${endpoint}`, {
      endpoint,
      method,
      duration,
      status,
      ...data,
    });
  }

  logSearch(searchParams: any, resultCount?: number, duration?: number): void {
    this.info(LogCategory.SEARCH, 'Search performed', {
      searchParams,
      resultCount,
      duration,
    });
  }

  logPerformance(metric: string, value: number, unit: string = 'ms'): void {
    this.info(LogCategory.PERFORMANCE, `Performance: ${metric}`, {
      metric,
      value,
      unit,
    });
  }

  logError(error: Error | string, context?: any): void {
    const errorData = {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } : error,
      context,
    };
    
    this.error(LogCategory.ERROR, 'Error occurred', errorData);
  }

  // Utility methods
  setUserId(userId: string): void {
    this.userId = userId;
    this.info(LogCategory.USER_INTERACTION, 'User identified', { userId });
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getLogs(category?: LogCategory, level?: LogLevel): LogEntry[] {
    return this.logs.filter(log => {
      if (category && log.category !== category) return false;
      if (level !== undefined && log.level < level) return false;
      return true;
    });
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  clearLogs(): void {
    this.logs = [];
    this.info(LogCategory.USER_INTERACTION, 'Logs cleared');
  }

  // Performance timing helpers
  startTimer(name: string): () => void {
    const startTime = performance.now();
    return () => {
      const duration = performance.now() - startTime;
     // this.logPerformance(name, duration);
      return duration;
    };
  }
}

// Create singleton instance
export const logger = new Logger();

// Export convenience functions
export const logUserInteraction = logger.logUserInteraction.bind(logger);
export const logNavigation = logger.logNavigation.bind(logger);
export const logApiCall = logger.logApiCall.bind(logger);
export const logSearch = logger.logSearch.bind(logger);
export const logPerformance = logger.logPerformance.bind(logger);
export const logError = logger.logError.bind(logger);
export const startTimer = logger.startTimer.bind(logger); 
export const logInfo = logger.info.bind(logger);
export const logDebug = logger.debug.bind(logger);